import type { components as NatalieApiComponents } from './generated/natalie-api';
import type { NATALIE_DURATIONS, SetupAnswerName } from 'pages/Natalie/NatalieAdmin/SelfServeCreate/Setup/setupSchema';

type Schemas = NatalieApiComponents['schemas'];

/**
 * Named aliases over the v2 Natalie creation contract, generated from
 * `api-specs/natalie.openapi.yaml`. Same indexed-access convention as
 * `natalieEvent.types.ts`, and the same reason: the alias is a name, never a
 * second declaration of the shape, so a spec change lands as a type error here
 * rather than as a silent disagreement with the service.
 *
 * Only the JWT-reachable surface is aliased. The spec also carries the prompt
 * registry (IAM-only) and the agent's resolved-config read (agent-key), copied
 * so this file converges with upstream — but no browser can authenticate
 * against them, so giving them names here would only invite a caller.
 */

// --- The event ---

/**
 * A list row. Everything true of an event that does not depend on what is
 * currently being edited.
 */
export type NatalieEventV2SummaryDto = Schemas['NatalieEventV2SummaryDto'];

/**
 * The event as the organiser sees it while working on it — the summary plus the
 * draft's agent and prompt configuration. Generated as an intersection with the
 * summary, so `revision` (which every write has to echo in `If-Match`) is
 * inherited rather than redeclared.
 */
export type NatalieEventV2Dto = Schemas['NatalieEventV2Dto'];

/**
 * The same read, as a caller who MAY MANAGE the event receives it.
 *
 * `prompt` and `context` are optional on the contract's own read because a community member who
 * cannot manage the event is served it WITHOUT them — absent rather than empty, deliberately, since
 * an empty value is a real state and would claim the event has nothing rather than that this reader
 * may not see it. (`draftBasedOnVersion` is held back the same way, and is already optional for its
 * own reasons.)
 *
 * This flow is organiser-only behind a community-admin gate, so every read it makes carries both.
 * Saying so once here is what keeps the screens and their fixtures from each guarding a case they
 * cannot reach — and it is a claim about WHO IS ASKING, so anything that starts serving this read
 * to a plain member must stop using this type rather than widen it.
 */
export type ManagedNatalieEventV2Dto = NatalieEventV2Dto & Required<Pick<NatalieEventV2Dto, 'context' | 'prompt'>>;

/**
 * The DRAFT face: `GET /v2/natalie/{eventId}/draft`.
 *
 * The live event's shape plus `draftBasedOnVersion` and `keptVoiceIds` (the voices this event keeps
 * beyond its live and draft voice), and the one this flow reads throughout — Review edits the draft,
 * so it reads the draft. Every write answers with it too (create, both `PUT`s, generate, publish,
 * rollback and reset), so a writer sees what it wrote without a second read. A v1 event has no draft
 * and answers 404 here.
 */
export type NatalieEventV2DraftDto = Schemas['NatalieEventV2DraftDto'];

/** The draft read as a caller who may manage the event receives it — see {@link ManagedNatalieEventV2Dto}. */
export type ManagedNatalieEventV2DraftDto = NatalieEventV2DraftDto &
  Required<Pick<NatalieEventV2DraftDto, 'context' | 'prompt'>>;

/**
 * The v2 lifecycle, which is v1's two values plus `DRAFT`.
 *
 * A SEPARATE enum from `NatalieEventStatus` rather than a third value added to it, and that is
 * load-bearing: the v1 enum is what the participant-facing reads narrow onto, and those decide
 * whether to offer a join — a state no draft has. A v1 event read over v2 is `ACTIVE` or
 * `COMPLETED`, never `DRAFT`.
 */
export type NatalieEventV2Status = Schemas['NatalieEventV2Status'];

/**
 * Runtime companion for {@link NatalieEventV2Status}.
 *
 * `DRAFT` is never published and never auto-closes, so an organiser whose draft end date has
 * passed can still move it and publish; the first publish makes it `ACTIVE`.
 */
export const NatalieEventV2Status = {
  Draft: 'DRAFT',
  Active: 'ACTIVE',
  Completed: 'COMPLETED',
} as const;

/** One page of `GET /v2/natalie`. */
export type NatalieEventV2PageDto = Schemas['NatalieEventV2PageDto'];

// --- The four configuration blocks ---

/**
 * What the organiser types. Versioned with the prompt, so an edit here stages
 * `hasUnpublishedChanges` and reaches participants only once it is published — with ONE exception,
 * `endDate`, which the join claim reads off the event itself and which therefore opens or closes
 * joining the moment it is written.
 *
 * `name` is unique within the community and `endDate` has to fall inside a window — in the future
 * and no more than 365 days out. Neither rule is expressible as a schema keyword, so neither is
 * visible in the type: both come back as a refusal, which is why both screens carry them too.
 */
export type EventConfigDto = Schemas['EventConfigDto'];

/** Runtime knobs for the conversation. Every value falls back to a service default. */
export type AgentConfigDto = Schemas['AgentConfigDto'];

/**
 * The template this event renders from, its greeting, and the values filling the body.
 *
 * The one versioned part of an event: a write here lands on the draft and reaches participants only
 * on publish. Everything else — event and agent configuration — is written in place.
 */
export type PromptDto = Schemas['PromptDto'];

/**
 * `{key}@{major}`, e.g. `interview@1` — a major line, never a concrete version. An event renders
 * from the newest minor of its major and picks up each new minor with no write. Fixed at creation
 * and not writable afterwards, which is why it is on the create body and not the update body.
 */
export type TemplateRef = Schemas['TemplateRef'];

/**
 * Field values keyed as the draft's template version declares them. Free-form
 * because the key set belongs to the template rather than to the API — the
 * contract's own `FieldDeclarationDto` schema is what says a key's type and
 * constraints, and nothing in this client reads a declaration yet. When
 * something does, note that a declaration's `public` flag — absent meaning not
 * public — is what decides whether an organiser sees a value at all.
 */
export type PromptFields = NonNullable<PromptDto['fields']>;

/**
 * Material handed to the model: whatever the intake form collected, stored as
 * supplied. Always sent wrapped as the `body` of a {@link ContextWriteItem}, on
 * create and on the live write alike, so the log has one shape rather than a
 * special case for its first entry.
 */
export type ContextEntryBody = Schemas['ContextEntryBody'];

/**
 * One item of a `context` write. Without `sequence` it appends a new entry; naming one edits that
 * entry in place and replaces its `body` WHOLE — a key left out of the new body is gone, not kept.
 * An edit that sends the body already stored is an echo, and leaves `editCount` alone.
 */
export type ContextWriteItem = Schemas['ContextWriteItem'];

/**
 * One entry in the context log, with the service-assigned metadata around it. `editCount` is how
 * many times its body has been replaced, which is what `prompt.contextApplied` is compared against
 * to tell an entry applied in its current form from one edited since.
 */
export type ContextEntryDto = Schemas['ContextEntryDto'];

// --- Generation ---

/**
 * Runtime companion for the generated `GenerationState` union — the generator
 * emits the type but no values, and the flow branches on these.
 */
export const GenerationState = {
  Running: 'RUNNING',
  Succeeded: 'SUCCEEDED',
  Failed: 'FAILED',
} as const;

export type GenerationState = Schemas['GenerationState'];

/**
 * Runtime companion for `GenerationErrorCode`. Codes only: the explanatory text
 * is the client's to write and translate, and text from the model provider is
 * never surfaced because it can echo the organiser's own context back.
 */
export const GenerationErrorCode = {
  ProviderError: 'PROVIDER_ERROR',
  TimedOut: 'TIMED_OUT',
  InsufficientContext: 'INSUFFICIENT_CONTEXT',
  TemplateError: 'TEMPLATE_ERROR',
} as const;

export type GenerationErrorCode = Schemas['GenerationErrorCode'];

/**
 * The last generation. Absent from the event entirely when none has ever run —
 * an event created with `fields` supplied directly never runs one, so there is
 * no state for "never generated".
 */
export type GenerationDto = Schemas['GenerationDto'];

/**
 * Body of `POST /v2/natalie/{eventId}/generate`, which says how to use the context log. `build`
 * writes a fresh draft from the brief with nothing of the current draft shown to the model; `amend`
 * applies only the entries added or edited since the draft was made. Refining is still two calls —
 * write the context, then ask for a generation.
 */
export type NatalieEventV2GenerateDto = Schemas['NatalieEventV2GenerateDto'];

// --- Writes ---

/**
 * Body of `POST /v2/natalie`. Two mutually exclusive shapes in one schema:
 * supply `context` and generation produces the field values, or supply `fields`
 * and none runs. Sending both is a 400.
 */
export type NatalieEventV2CreateDto = Schemas['NatalieEventV2CreateDto'];

/** `201` from create — the event, plus an advisory duplicate warning when one applies. */
export type NatalieEventV2CreatedDto = Schemas['NatalieEventV2CreatedDto'];

/**
 * A recently created event in the same community with matching CONTEXT — "you may already have
 * asked this". Advisory: it rides along with a 201 and never blocks anything.
 *
 * Not to be confused with `DUPLICATE_NAME`, which is a refusal and does block. Different subject
 * (the name, not the context) and different outcome, so a client that treats this as the duplicate
 * check would let a colliding name through to a 400.
 */
export type DuplicateWarningDto = Schemas['DuplicateWarningDto'];

/**
 * Body of `PUT /v2/natalie/{eventId}` — a merge-patch. A part omitted is left
 * untouched, and within a part a member omitted is left as stored, so a write
 * names only what changed and `null` is what clears a member.
 *
 * `status` is writable and is the ONLY way to end an event early: `endDate`
 * cannot be moved into the past — nor more than 365 days out — and overwriting
 * it with the current time would destroy the scheduled date the reminder email
 * and the public event read both show. Both bounds are skipped when the write
 * leaves the stored date alone, so echoing back a value a read gave you is
 * always safe. On an event that is not closed the value sent is honoured; on a closed
 * one it is ignored and re-derived from `endDate`, so a client that replays a
 * stale `COMPLETED` while merging cannot strand the event.
 *
 * No `templateRef`: the major is fixed at creation.
 */
export type NatalieEventV2UpdateDto = Schemas['NatalieEventV2UpdateDto'];

/**
 * Body of `PUT /v2/natalie/{eventId}/draft` — everything a published version freezes.
 *
 * The split is what the two `PUT`s are FOR: `eventConfig`, `agentConfig` and `prompt` are the
 * draft's and go here, while the context log and the lifecycle are the event's only copy and go
 * to the live `PUT`. Sending a versioned block to the live route is a 400 naming the member, so
 * the two bodies are not interchangeable and the type will not let them be.
 */
export type NatalieEventV2DraftUpdateDto = Schemas['NatalieEventV2DraftUpdateDto'];

/** Body of `POST /v2/natalie/{eventId}/publish`. `note` is shown in the version list. */
export type PublishRequestDto = Schemas['PublishRequestDto'];

// --- Published versions ---

/** A row in the version list. */
export type EventVersionSummaryDto = Schemas['EventVersionSummaryDto'];

/** A published version in full: the prompt, the event configuration and the agent configuration, which is all three of the parts a version freezes. */
export type EventVersionDto = Schemas['EventVersionDto'];

/** `GET /v2/natalie/{eventId}/versions`. `publishedVersion` is not necessarily the highest number — it differs after a rollback. */
export type EventVersionListDto = Schemas['EventVersionListDto'];

/** Body of `POST /v2/natalie/{eventId}/rollback`. */
export type RollbackRequestDto = Schemas['RollbackRequestDto'];

// --- Voices ---

/**
 * One voice, named the way a picker shows it. `offered` marks the voices the self-serve picker
 * lists. River and every other vetted voice come back `false`, and are listed only so a picker can
 * name a voice an event already uses.
 */
export type NatalieVoiceDto = Schemas['NatalieVoiceDto'];

/**
 * `GET /v2/natalie/voices`, the same for every caller: the offered voices in picker order, then
 * River, then every other vetted voice.
 */
export type NatalieVoiceListDto = Schemas['NatalieVoiceListDto'];

/**
 * Body of `POST /v2/natalie/{eventId}/voice-preview`. `text` is the introduction as it stands on
 * the form, saved or not, up to the same 4096 bytes `prompt.greeting` allows.
 */
export type NatalieVoicePreviewRequestDto = Schemas['NatalieVoicePreviewRequestDto'];

/**
 * The clip, as base64 MP3 inside JSON. Synthesised afresh on every call, and about 3 MB encoded for
 * a full introduction.
 */
export type NatalieVoicePreviewDto = Schemas['NatalieVoicePreviewDto'];

// --- Setup templates ---
//
// Starting points for Setup, and unrelated to the prompt template above (`TemplateRef`), which is
// why every name here says `SetupTemplate`. Only the list is reachable with a community admin's
// token; the writes are for Natter staff over IAM.

/**
 * A template as the list serves it: what its card and details show (`overview`) and what it
 * prefills Setup with (`blueprint`). Never archived on the list, and without `createdBy` and
 * `updatedBy`, which only the staff read carries.
 */
export type SetupTemplateDto = Schemas['SetupTemplateDto'];

/**
 * `GET /v2/natalie/setup-templates?communityId=`: Natter's templates, then the community's own,
 * each oldest first, which is the chooser's order. An empty `items` is an answer, and sends Create
 * straight to the blank Setup page.
 */
export type SetupTemplateListDto = Schemas['SetupTemplateListDto'];

export type SetupTemplateBlueprintDto = Schemas['SetupTemplateBlueprintDto'];

/** One question on Setup's first step. The wording names it as `{id}`. */
export type SetupTemplateQuestionDto = Schemas['SetupTemplateQuestionDto'];

/*
 * The two value sets the contract shares with Setup, which has an answer for each brief key and
 * offers the same lengths. A value only one side knows is a template Setup can't show, which is why
 * the spec orders how each set changes.
 */
export type SetupTemplateBriefKey = Schemas['SetupTemplateBriefKey'];
type SetupTemplateLengthMinutes = Schemas['SetupTemplateLengthMinutes'];

/**
 * Body of `POST /v2/natalie/{eventId}/setup-use`: how Setup set up a Natalie, from a template or
 * from Start blank, sent once after the create for product analytics. Choices and word counts only,
 * never text the admin typed. A Natalie keeps its first record, and a second is refused
 * `SETUP_USE_ALREADY_RECORDED`.
 */
export type SetupUseRecordDto = Schemas['SetupUseRecordDto'];

// --- Errors ---

/**
 * Runtime companion for `V2ErrorCode`, and the only thing worth branching on:
 * `409` alone covered "a generation is running" (wait and retry), "nothing to
 * publish" (never retry) and a stale revision (re-read, then re-apply) with no
 * way to tell them apart.
 */
export const V2ErrorCode = {
  StaleRevision: 'STALE_REVISION',
  GenerationRunning: 'GENERATION_RUNNING',
  NothingToPublish: 'NOTHING_TO_PUBLISH',
  NeverPublished: 'NEVER_PUBLISHED',
  EventConfigIncomplete: 'EVENT_CONFIG_INCOMPLETE',
  FieldConstraintFailed: 'FIELD_CONSTRAINT_FAILED',
  TemplateRenderFailed: 'TEMPLATE_RENDER_FAILED',
  EventNotActive: 'EVENT_NOT_ACTIVE',
  JoinLimitReached: 'JOIN_LIMIT_REACHED',
  AgentDispatchFailed: 'AGENT_DISPATCH_FAILED',
  /*
   * The dispatch and render routes' own two, added to the contract by the September report sync.
   *
   * Named here because this companion is a mirror of the declared set and `EveryV2ErrorCodeIsNamed`
   * holds it to that — but deliberately given no copy anywhere in the self-serve flow. Both are
   * declared on `/v2/natalie/{eventId}/draft/dispatch` and `/render`, which this flow never calls:
   * `NOT_V2` says the event is joined on v1 and has no v2 draft to play, and
   * `DRAFT_NOT_RENDERABLE` carries a `refusals` list the organiser-facing screens have no surface
   * for. If either ever did arrive here, the failure tables fall through to their `generic` line,
   * which is the right answer for a refusal this flow cannot explain.
   */
  NotV2: 'NOT_V2',
  DraftNotRenderable: 'DRAFT_NOT_RENDERABLE',
  RequestInFlight: 'REQUEST_IN_FLIGHT',
  RequestKeyReused: 'REQUEST_KEY_REUSED',
  ValidationError: 'VALIDATION_ERROR',
  DuplicateName: 'DUPLICATE_NAME',
  NotFound: 'NOT_FOUND',
  Forbidden: 'FORBIDDEN',
  PermissionsUnavailable: 'PERMISSIONS_UNAVAILABLE',
  PermissionsRequestRejected: 'PERMISSIONS_REQUEST_REJECTED',
  ServiceUnavailable: 'SERVICE_UNAVAILABLE',
  /*
   * The voice preview's own three. ElevenLabs failed or sent no audio (502), or did not finish
   * within 20 seconds (504), and each is worth one retry. Or this environment has no ElevenLabs key
   * yet (503), which a retry cannot fix.
   */
  VoicePreviewFailed: 'VOICE_PREVIEW_FAILED',
  VoicePreviewTimedOut: 'VOICE_PREVIEW_TIMED_OUT',
  VoicePreviewNotConfigured: 'VOICE_PREVIEW_NOT_CONFIGURED',
  /*
   * A `generate` asked to amend a draft that cannot be amended: it carries no record of what it was
   * built from, or its template has no amend path. The same call with `mode: build` succeeds. This
   * flow only ever builds, so it has no copy for this one.
   */
  BuildRequired: 'BUILD_REQUIRED',
  /*
   * The two a draft delete adds: the event has been published (409), or reporting-api could not
   * list or delete the draft's report (502). The row menu shows one generic failure for both.
   */
  AlreadyPublished: 'ALREADY_PUBLISHED',
  ReportingServiceError: 'REPORTING_SERVICE_ERROR',
  /*
   * A setup-use record for a Natalie that already has one (409). The first record stands. The
   * record is sent without waiting on its answer, so no screen has copy for this one.
   */
  SetupUseAlreadyRecorded: 'SETUP_USE_ALREADY_RECORDED',
  /*
   * A quotes change the caller may not make (403): not an org admin, or the first reportable
   * conversation has landed since the screen read the Natalie. Review names it through
   * `reportQuotes.ts`, which matches the same string.
   */
  ReportQuotesNotEditable: 'REPORT_QUOTES_NOT_EDITABLE',
  InternalError: 'INTERNAL_ERROR',
} as const;

export type V2ErrorCode = Schemas['V2ErrorCode'];

type Never<T extends never> = T;

/**
 * Proof that the companion above names every code the contract declares. `extractV2ErrorCode`
 * drops a code it cannot find in the companion, so one missed while syncing the spec reaches
 * the organiser as an unnamed failure instead of the sentence written for it — and nothing at
 * runtime would say so. A code added upstream and not added above leaves this alias something
 * other than `never`, and the type-check fails on this line.
 */
export type EveryV2ErrorCodeIsNamed = Never<Exclude<V2ErrorCode, (typeof V2ErrorCode)[keyof typeof V2ErrorCode]>>;

/** The v2 error envelope. Same shape as v1's, with `errorId` typed as a closed set. */
export type V2ErrorResponse = Schemas['V2ErrorResponse'];

/** A single error out of the envelope — what a refusal message is rendered from. */
export type V2Error = V2ErrorResponse['errors'][number];

// --- The sets Setup shares with setup templates ---

/** The values only one of two sets has: `never` exactly when the sets are equal. */
type SymmetricDifference<A, B> = Exclude<A, B> | Exclude<B, A>;

type SetupLength = (typeof NATALIE_DURATIONS)[number];

/**
 * Proof that Setup's four written answers are the four keys of a template's brief. Setup puts each
 * brief entry's text in the answer of the same name, so a key only the spec has is an entry Setup
 * cannot show, and an answer only Setup has is one no template can fill. Either leaves this alias
 * something other than `never`, and the type-check fails on this line.
 */
export type SetupAnswersAreTheBriefKeys = Never<SymmetricDifference<SetupTemplateBriefKey, SetupAnswerName>>;

/**
 * Proof that Setup's length select offers exactly the lengths a template may start on. A length
 * only the spec has would start Setup on a value its select cannot show, and one only Setup has is
 * one no template can be written with. Either fails the type-check here, as above.
 */
export type SetupLengthsAreTheTemplateLengths = Never<SymmetricDifference<SetupTemplateLengthMinutes, SetupLength>>;
